<template>
    <div v-if="options.showDialog">
        <v-dialog v-model="options.showDialog" persistent max-width="900" content-class="user-prefs-dialog">
            <v-card class="user-prefs-card">
                <v-card-title class="headline">{{$t('prefs.dialogTitle')}}</v-card-title>
                <v-card-text>
                        <v-container class="topContainer">
                            <v-row>
                                <v-col cols="6">
                                    <v-select v-model="globalPrefs.idName" filled :label="$t('prefs.idAndNameDisplay')" :items="idAndNameOptions"/>
                                    <v-select v-model="globalPrefs.enableGraphLineMarkers" filled :label="$t('prefs.graphLineMarkers')" :items="graphLineMarkerOptions" />
                                    <v-select v-model="globalPrefs.unitsType" filled :label="$t('prefs.units')" :items="prefsUnits"/>
                                    <v-select v-model="globalPrefs.mapExportIncludeLabels" filled :label="$t('prefs.mapExportIncludeLabels')" :items="mapExportIncludeLabelsOptions"/>
                                    <v-select v-model="globalPrefs.snackbarDisplayTimeout" filled :label="$t('prefs.toasterMsgTime')" :items="snackbarDisplayTimeoutOptions" />
                                </v-col>
                                <v-col cols="6">
                                    <v-select v-model="globalPrefs.duration" filled :label="$t('prefs.duration.title')" :items="durationOptions" :item-value="duration.interval" :placeholder="$t('prefs.duration.placeholder')" return-object/>
                                    <v-select v-model="globalPrefs.enableMapBillboardsToDots" filled :label="$t('prefs.mapBillboardsToDots')" :items="mapBillboardsToDotsOptions" />
                                    <v-select v-model="globalPrefs.clearMapCacheOnExit" filled :label="$t('prefs.mapClearCacheOnExit')" :items="clearMapCacheOnExitOptions"/>
                                    <v-select v-model="globalPrefs.defaultRadiosTable" filled :label="$t('prefs.defaultRadiosTable')" :items="radiosTableOptions" />
                                    <v-select v-if="Auth().isNorthAmerica()" v-model="globalPrefs.pageRefreshInterval" filled :label="$t('prefs.pageRefreshInterval')" :items="pageRefreshIntervals" />
                                </v-col>
                            </v-row>
                        </v-container>
                        <v-container class="colorsContainer">
                            <fieldset class="colors-fieldset">
                                <legend>{{ $t('prefs.colors') }}</legend>
                                <div class="pref-control">
                                    <v-select v-model="globalPrefs.colorTheme" filled :label="$t('prefs.colorTheme')" :items="colorThemeOptions"/>
                                </div>
                                <div :class="globalPrefs.colorTheme != 'NCSDark'? 'pref-view-light' : 'pref-view-dark'">
                                    <div class="color-theme-container">
                                        <div class="color-label">{{$t('prefs.alarmColorsLabel')}}</div>
                                        <div class="color-values">
                                            <v-chip v-for="(color) in alarmColors" :key="color.key" variant="flat"
                                                    class="alarm-color" :color="color.value">{{$t('prefs.alarmColors.' + color.key)}}</v-chip>
                                        </div>
                                    </div>
                                    <div class="color-theme-container">
                                        <div class="color-label">{{$t('prefs.uiColorsLabel')}}</div>
                                        <div class="color-values">
                                            <span v-for="(color, index) in uiColors" :key="index">
                                            <v-chip v-if="index < 7" :key="index" variant="flat"
                                                    class="ui-color" :color="color.value">{{$t('prefs.uiColors.' + color.key)}}</v-chip>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="color-theme-container">
                                        <div class="color-label">{{ $t('prefs.graphColorsLabel') }}</div>
                                        <div class="color-values">
                                            <v-chip v-for="(color) in graphColors" :key="color.key" variant="flat"
                                                    class="graph-color" :color="color.value"/>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="warning" text class="cancel-button" @click="cancel">{{ $t('common.cancel') }}</v-btn>
                    <v-btn color="primary" text class="save-button" @click="save">{{ $t('common.save') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>


<script lang="js">
    import NCSThemes from "../../style/NCSThemes"
    import DurationDefaults from '../common/DurationDefaults'
    import {TABLE_KEYS} from "../radios/metrics/RadioTableUtility"
    import Auth from "../../services/auth"
    import PrefsService from "../../services/PrefsService"
    import {useTheme} from "vuetify"
    import GlobalPrefsService from "../../services/globalPrefsService";

    export const PREFS_UNITS_TYPE = Object.freeze({
        METRIC: "METRIC",
        IMPERIAL: "IMPERIAL"
    })

    export default {
        name: 'GlobalPrefsDialog',
        mixins: [DurationDefaults],
        props: {
            options: {
                type: Object,
                required: true
            }
        },
        setup() {
            // This is needed to use the theme in the template
            return { theme: useTheme() }
        },
        data () {
            return {
                userPrefsKey: 'globalPrefs',
                prefsUnits: [PREFS_UNITS_TYPE.METRIC, PREFS_UNITS_TYPE.IMPERIAL],
                userMessage: null,
                idName: 'id',
                duration: {id: 'last24Hours', interval: 'HOUR'},
                globalPrefs: {
                    idName: 'both',
                    enableGraphLineMarkers: true,
                    enableMapBillboardsToDots: true,
                    clearMapCacheOnExit: false,
                    mapExportIncludeLabels: false,
                    snackbarDisplayTimeout : 5000,
                    pageRefreshInterval: 0,
                    colorTheme: 'NCSLight',
                    duration: {},
                    unitsType: PREFS_UNITS_TYPE.METRIC,
                    defaultRadiosTable: null
                },
            }
        },
        watch: {
            showDialog (newVal) {
                if (newVal) {
                    this.getGlobalPrefs()
                }
            }
        },
        created() {
            this.globalPrefs.duration = this.getDurationPrefOptions()[0]
        },
        computed: {
            showDialog ()
            {
                return this.options.showDialog
            },
            selectedThemeColors ()
            {
                // Reference the reactive selection so the preview updates immediately
                // when the user changes the theme, before it is saved to PrefsService,
                // while reusing the service's theme validation/fallback logic.
                let colorTheme = GlobalPrefsService.resolveColorTheme(this.globalPrefs.colorTheme)
                return NCSThemes[colorTheme].colors
            },
            alarmColors ()
            {
                const themeColors = this.selectedThemeColors
                return Object.entries(themeColors).filter(([key]) => key.startsWith('alarm-')).map(([key, value]) => ({ key, value }));
            },
            graphColors ()
            {
                const themeColors = this.selectedThemeColors
                return Object.entries(themeColors).filter(([key]) => key.startsWith('series-')).map(([key, value]) => ({ key, value }));
            },
            uiColors ()
            {
                const themeColors = this.selectedThemeColors
                let tc = Object.entries(themeColors).filter(([key]) => !key.startsWith('alarm-') && !key.startsWith('series-')).map(([key, value]) => ({ key, value }));
                return tc
            },
            idAndNameOptions ()
            {
                return [
                    {value: 'id', title: this.$t('prefs.idAndNameOptions.id')},
                    {value: 'name', title: this.$t('prefs.idAndNameOptions.name')},
                    {value: 'both', title: this.$t('prefs.idAndNameOptions.both')}
                ]
            },
            graphLineMarkerOptions () {
                return [
                    {value: false, title: this.$t('prefs.graphLineMarkerOptions.disable')},
                    {value: true, title: this.$t('prefs.graphLineMarkerOptions.enable')}
                ]
            },
            mapBillboardsToDotsOptions () {
                return [
                    {value: false, title: this.$t('prefs.mapBillboardsToDotsOptions.disable')},
                    {value: true, title: this.$t('prefs.mapBillboardsToDotsOptions.enable')}
                ]
            },
            clearMapCacheOnExitOptions () {
                return [
                    {value: false, title: this.$t('prefs.clearMapCacheOnExitOptions.disable')},
                    {value: true, title: this.$t('prefs.clearMapCacheOnExitOptions.enable')}
                ]
            },
            mapExportIncludeLabelsOptions () {
                return [
                    {value: false, title: this.$t('prefs.mapExportIncludeLabelsOptions.disable')},
                    {value: true, title: this.$t('prefs.mapExportIncludeLabelsOptions.enable')}
                ]
            },
            snackbarDisplayTimeoutOptions() {
                return [
                    { value: 5000, title: '5 Seconds' },
                    { value: 15000, title: '15 Seconds' },
                    { value: 30000, title: '30 Seconds' },
                    { value: 60000, title: '60 Seconds' },
                ]
            },
            pageRefreshIntervals () {
                return [
                    {value: 0, title: 'Off, No Auto Refresh'},
                    {value: 1, title: '1 Minute'},
                    {value: 5, title: '5 Minutes'},
                    {value: 15, title: '15 Minutes'}
                ]
            },
            colorThemeOptions ()
            {
                let themeKeys = Object.keys(NCSThemes)
                let colorThemes = []
                themeKeys.forEach(key => {
                    if (key.startsWith('NCS'))
                    {
                        colorThemes.push({value: key, title: this.$t('prefs.colorThemes.' + key)})
                    }
                })
                return colorThemes
            },
            durationOptions() {
                return this.getDurationPrefOptions()
            },
            radiosTableOptions() {
                if (Auth.isArqiva()) {
                    return [
                        {title: this.$t(TABLE_KEYS.STATUS), value: TABLE_KEYS.STATUS},
                        {title: this.$t(TABLE_KEYS.STATISTICS), value:TABLE_KEYS.STATISTICS},
                        {title: this.$t(TABLE_KEYS.CONFIG), value:TABLE_KEYS.CONFIG},
                        {title: this.$t(TABLE_KEYS.COMMAND_CHANNELS), value:TABLE_KEYS.COMMAND_CHANNELS},
                        {title: this.$t(TABLE_KEYS.BULK_CHANNELS), value:TABLE_KEYS.BULK_CHANNELS},
                        {title: this.$t('common.' + TABLE_KEYS.ALL), value: TABLE_KEYS.ALL},
                        {title: this.$t('common.notset'), value: null}
                    ]
                } else if (Auth.isNorthAmerica()) {

                    let tableList = [
                        {title: this.$t(TABLE_KEYS.NA_STATUS), value: TABLE_KEYS.NA_STATUS},
                        {title: this.$t(TABLE_KEYS.NA_STATISTICS), value:TABLE_KEYS.NA_STATISTICS},
                        {title: this.$t(TABLE_KEYS.NA_CONFIG), value:TABLE_KEYS.NA_CONFIG},
                        {title: this.$t(TABLE_KEYS.NA_CHANNELS), value:TABLE_KEYS.NA_CHANNELS},
                        {title: this.$t(TABLE_KEYS.NA_DEVICES), value:TABLE_KEYS.NA_DEVICES},
                        {title: this.$t('common.' + TABLE_KEYS.NA_ALL), value: TABLE_KEYS.NA_ALL},
                        {title: this.$t('common.notset'), value: null}
                    ]

                    if (Auth.isTuningFeatureEnabled()) {
                        tableList.splice(tableList.length - 2, 0, 
                            {title: this.$t(TABLE_KEYS.NA_TUNING), value:TABLE_KEYS.NA_TUNING},
                        )
                    }
                    return tableList
                }
            }
        },
        methods: {
            Auth()
            {
                return Auth
            },
            save ()
            {
                this.theme.change(this.globalPrefs.colorTheme)
                PrefsService.put(this.userPrefsKey, JSON.stringify(this.globalPrefs))
                PrefsService.put ('colorTheme', this.globalPrefs.colorTheme)
                this.options.showDialog = false
                this.$emit('globalPrefsChanged')
            },
            cancel ()
            {
                this.options.showDialog = false
            },
            getGlobalPrefs ()
            {
                let prefs = PrefsService.get(this.userPrefsKey)
                if (prefs)
                {
                    this.globalPrefs = JSON.parse(prefs)
                    this.globalPrefs.colorTheme = GlobalPrefsService.getColorTheme() // This makes sure the theme is set correctly
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .user-prefs-dialog {
        font-size: 13px;

        .topContainer {
            padding-top: 0;
            padding-bottom: 0;
        }
        .colorsContainer {
            padding-top: 0;
            padding-bottom: 0;
        }

        .colors-fieldset {
            padding: 20px 20px;
            border: 1px solid #E5E6E8 !important;
            border-radius: 3px;
        }

        .color-theme-container {
            display: flex;
            align-items: center;
            .color-label {
                width: 130px;
                min-width: 130px;
                color: #000000;
            }
        }

        .pref-view-light {
            padding: 15px;
            border: 1px solid #E5E6E8;
            background-color: #ffffff;
        }

        .pref-view-dark {
            padding: 15px;
            border: 1px solid #E5E6E8;
            background-color: #44475a
        }

        .alarm-color, .ui-color {
            margin: 4px;
        }
        .graph-color {
            height: 40px;
            width: 40px;
            border-radius: 50%;
            margin: 4px;
        }
    }
</style>
